import { Button, Checkbox, Flex, ScrollArea, Stack, Text } from '@mantine/core';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ExecuteRequestPanel from './ExecuteRequestPanel';
import { COLUMN_HEIGHT, ExplorerPanel } from './ExplorerParts';
import { applyModuleDependencies } from './module-dependencies';
import { compareModules, type ModuleIndex } from './module-filter';
import { getModuleLabel, isModuleId, type ModuleId } from './module-labels';
import ModuleExampleSnippet from './ModuleExampleSnippet';
import './ModuleExplorer.css';
import { buildSpecTree, computeHighlights, countModuleFields, type SpecNode, type SpecOperation } from './spec-tree';
import { useSpecTreeNavigation } from './spec-tree-navigation';
import SpecNodeDetails from './SpecNodeDetails';
import SpecTreeView from './SpecTreeView';

type ModuleExplorerProps = {
  calculator: string;
  version: string | undefined;
  spec: Record<string, unknown>;
  moduleIndex: ModuleIndex;
  selectedModules: ModuleId[];
  onSelectedModulesChange: (modules: ModuleId[]) => void;
};

type ModuleCheckboxProps = {
  module: ModuleId;
  fieldCount: number;
  checked: boolean;
  /** Why the module was just selected or deselected automatically, if it was */
  reason: string | undefined;
  onToggle: (module: ModuleId, checked: boolean) => void;
};

// Memoised so toggling one module only re-renders the checkboxes that changed, rather than the whole list
const ModuleCheckbox = memo(function ModuleCheckbox({
  module,
  fieldCount,
  checked,
  reason,
  onToggle,
}: ModuleCheckboxProps) {
  return (
    <Checkbox
      checked={checked}
      onChange={(event) => onToggle(module, event.currentTarget.checked)}
      classNames={{
        labelWrapper: 'module-checkbox-label-wrapper',
        label: 'module-checkbox-label',
        input: reason ? 'module-auto-changed-input' : undefined,
      }}
      label={
        <>
          {getModuleLabel(module)}{' '}
          <Text span size="xs" c="dimmed">
            ({fieldCount})
          </Text>
          {/* Laid over the label rather than below it, so the list never changes height */}
          {reason && <span className="module-auto-changed-reason">{reason}</span>}
        </>
      }
    />
  );
});

// Open each operation and its request body, which is where the module tagged fields live
const startsExpanded = (node: SpecNode) => node.kind === 'operation' || node.kind === 'requestBody';

// How long an automatically selected module stays highlighted, matching the animation in ModuleExplorer.css
const AUTO_SELECTED_HIGHLIGHT_MS = 3000;

function ModuleExplorer({
  calculator,
  version,
  spec,
  moduleIndex,
  selectedModules,
  onSelectedModulesChange,
}: ModuleExplorerProps) {
  // The request being executed, shown as a layer drilled into from the columns
  const [execution, setExecution] = useState<{ operation: SpecOperation; body: unknown } | null>(null);
  const [hasReturned, setHasReturned] = useState(false);
  // Modules just selected or deselected because of a module that requires them, mapped to an explanation. The
  // nonce remounts the checkboxes so the animation replays if the same module changes automatically again.
  const [autoChanged, setAutoChanged] = useState<{ reasons: Map<ModuleId, string>; nonce: number }>({
    reasons: new Map(),
    nonce: 0,
  });

  useEffect(() => {
    if (autoChanged.reasons.size === 0) return undefined;
    const timeout = setTimeout(
      () => setAutoChanged((current) => ({ ...current, reasons: new Map() })),
      AUTO_SELECTED_HIGHLIGHT_MS,
    );
    return () => clearTimeout(timeout);
  }, [autoChanged]);

  const changeSelectedModules = (value: string[]) => {
    const { modules, added, removed } = applyModuleDependencies(
      selectedModules,
      value.filter(isModuleId),
      moduleIndex.modules,
    );
    setAutoChanged((current) => ({
      reasons: new Map([
        ...added.map(({ module, requiredBy }) => [module, `Selected because ${requiredBy} requires it`] as const),
        ...removed.map(({ module, requiredBy }) => [module, `Deselected along with ${requiredBy}`] as const),
      ]),
      nonce: current.nonce + 1,
    }));
    onSelectedModulesChange(modules.sort(compareModules));
  };

  const specTree = useMemo(() => buildSpecTree(spec, moduleIndex), [spec, moduleIndex]);
  const fieldCounts = useMemo(() => countModuleFields(specTree), [specTree]);
  const selectedSet = useMemo(() => new Set(selectedModules), [selectedModules]);
  const highlights = useMemo(
    () => (selectedSet.size > 0 ? computeHighlights(specTree, selectedSet) : null),
    [specTree, selectedSet],
  );

  const { tree, selectedNode, treeViewport, detailsViewport, navigateTo } = useSpecTreeNavigation(
    specTree,
    startsExpanded,
  );

  const expandToMatches = () => {
    if (!highlights) return;
    tree.setExpandedState((current) => {
      const next = { ...current };
      highlights.forEach((highlight, value) => {
        if (highlight === 'contains') next[value] = true;
      });
      return next;
    });
  };

  const matchCount = useMemo(
    () => (highlights ? [...highlights.values()].filter((highlight) => highlight === 'match').length : 0),
    [highlights],
  );

  // The checkboxes are memoised, so they get a callback that never changes but always sees the current selection
  const latestToggle = useRef<(module: ModuleId, checked: boolean) => void>(() => undefined);
  useLayoutEffect(() => {
    latestToggle.current = (module, checked) =>
      changeSelectedModules(checked ? [...selectedModules, module] : selectedModules.filter((m) => m !== module));
  });
  const toggleModule = useCallback((module: ModuleId, checked: boolean) => latestToggle.current(module, checked), []);

  const executeRequest = useCallback(
    (operation: SpecOperation, body: unknown) => setExecution({ operation, body }),
    [],
  );

  const operations = useMemo(
    () => [...specTree.nodes.values()].flatMap((node) => (node.operation ? [node.operation] : [])),
    [specTree],
  );

  return (
    <>
      {execution && (
        <ExecuteRequestPanel
          calculator={calculator}
          version={version}
          operation={execution.operation}
          initialBody={execution.body}
          onBack={() => {
            setExecution(null);
            setHasReturned(true);
          }}
        />
      )}
      {/* Kept mounted while executing so the tree selection and expansion survive the round trip */}
      <Flex
        gap="md"
        h={COLUMN_HEIGHT}
        display={execution ? 'none' : 'flex'}
        className={`module-explorer${hasReturned ? ' module-explorer-returned' : ''}`}
      >
        <ExplorerPanel
          title="Modules"
          w={280}
          miw={220}
          style={{ flexShrink: 0 }}
          actions={
            <>
              <Button
                variant="subtle"
                size="compact-xs"
                disabled={selectedModules.length === moduleIndex.modules.length}
                onClick={() => onSelectedModulesChange([...moduleIndex.modules].sort(compareModules))}
              >
                Select all
              </Button>
              <Button
                variant="subtle"
                size="compact-xs"
                disabled={selectedModules.length === 0}
                onClick={() => onSelectedModulesChange([])}
              >
                Clear
              </Button>
            </>
          }
        >
          <ScrollArea flex={1} p="sm">
            <Stack gap="xs" role="group" aria-label="Modules">
              {moduleIndex.modules.map((module) => {
                const reason = autoChanged.reasons.get(module);
                return (
                  <ModuleCheckbox
                    // A new key remounts the checkbox, so the animation replays each time it changes automatically
                    key={reason ? `${module}-${autoChanged.nonce}` : module}
                    module={module}
                    fieldCount={fieldCounts.get(module) ?? 0}
                    checked={selectedSet.has(module)}
                    reason={reason}
                    onToggle={toggleModule}
                  />
                );
              })}
            </Stack>
          </ScrollArea>
        </ExplorerPanel>

        <ExplorerPanel
          title="Schema"
          description={highlights && `${matchCount} matching ${matchCount === 1 ? 'field' : 'fields'}`}
          flex={1}
          miw={0}
          actions={
            <>
              <Button variant="subtle" size="compact-xs" disabled={!highlights} onClick={expandToMatches}>
                Expand matches
              </Button>
              <Button variant="subtle" size="compact-xs" onClick={() => tree.collapseAllNodes()}>
                Collapse all
              </Button>
            </>
          }
        >
          <ScrollArea flex={1} p="xs" viewportRef={treeViewport}>
            <SpecTreeView
              specTree={specTree}
              tree={tree}
              scrollRef={treeViewport}
              highlights={highlights}
              selectedModules={selectedSet}
            />
          </ScrollArea>
        </ExplorerPanel>

        <Stack flex={1} miw={0} gap="md">
          <ExplorerPanel title="Details" flex={1} mih={0}>
            <ScrollArea flex={1} p="md" viewportRef={detailsViewport}>
              {selectedNode ? (
                <SpecNodeDetails
                  node={selectedNode}
                  nodes={specTree.nodes}
                  highlights={highlights}
                  selectedModules={selectedSet}
                  onNavigate={navigateTo}
                />
              ) : (
                <Text c="dimmed" size="sm">
                  Select a node in the schema tree to see its details.
                </Text>
              )}
            </ScrollArea>
          </ExplorerPanel>

          <ModuleExampleSnippet
            spec={spec}
            moduleIndex={moduleIndex}
            operations={operations}
            selectedModules={selectedSet}
            onExecuteRequest={executeRequest}
          />
        </Stack>
      </Flex>
    </>
  );
}

export default ModuleExplorer;
